import { useCallback, useEffect, useState } from "react";
import { apiJson } from "../utils/api.js";
import { setAppDisplayTimezone } from "../utils/displayTimezone.js";

let cachedMe = null;
let cachedAt = 0;
const CACHE_MS = 30_000;

export function useMe() {
  const [me, setMe] = useState(cachedMe);
  const [loading, setLoading] = useState(!cachedMe);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    cachedMe = null;
    cachedAt = 0;
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = Date.now();
      if (tick === 0 && cachedMe && now - cachedAt < CACHE_MS) {
        setAppDisplayTimezone(cachedMe?.iana_timezone);
        setMe(cachedMe);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      try {
        const data = await apiJson("/api/me");
        if (cancelled) return;
        setAppDisplayTimezone(data?.iana_timezone);
        cachedMe = data;
        cachedAt = Date.now();
        setMe(data);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setMe(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const isAdmin = me?.role === "admin";
  const isRop = me?.role === "rop";
  const isModerator = me?.role === "moderator";
  const isManagerStaff =
    !!me &&
    !me.is_dealer &&
    me.role !== "admin";
  const canViewReports =
    !!me &&
    !me.is_dealer &&
    (me.role === "admin" ||
      me.role === "manager" ||
      me.role === "rop" ||
      me.role === "moderator");
  const canTransferAssignments =
    !!me &&
    !me.is_dealer &&
    (me.role === "admin" || me.role === "rop" || me.role === "moderator");

  return {
    me,
    loading,
    error,
    isAdmin,
    isRop,
    isModerator,
    isManagerStaff,
    canViewReports,
    canTransferAssignments,
    refetch,
  };
}
